import request from './request'
import { Driver, DriverList, DriverCarList, DriverCar, CarDocument, DriverDocument } from './types/driver'
import { DriverApplicationDocument, DriverApplicationDocumentType } from './types/driver-application'
import { AxiosResponse } from 'axios'

export interface DriversSearchParams {
  query?: string
  offset?: number
  limit?: number
  carType?: string
  number_of_seats?: number
  luggage_places?: number
}

export const getDrivers = async ({
  query,
  offset,
  limit,
  carType,
  number_of_seats,
  luggage_places,
}: DriversSearchParams = {}): Promise<AxiosResponse<DriverList>> => {
  const params = new URLSearchParams()
  if (offset) params.append('offset', offset?.toString())
  if (limit) params.append('limit', limit?.toString())
  if (luggage_places) params.append('luggage_places', luggage_places?.toString())
  if (number_of_seats) params.append('number_of_seats', number_of_seats?.toString())
  if (carType) params.append('car_type_', carType)
  if (query) {
    params.append('first_name', query)
    // params.append('last_name', query)
  }
  const response = await request.get<DriverList>(`/drivers/?${params.toString()}`)
  return response
}

export const getDriver = async (id: number): Promise<AxiosResponse<Driver>> => {
  const data = await request.get<Driver>(`/drivers/${id}/`)
  return data
}

export const deleteDriver = async (driverId: number): Promise<AxiosResponse<void>> => {
  return request.delete<void>(`/drivers/${driverId}/`)
}

export const getMyDriverProfile = async (): Promise<AxiosResponse<Driver>> => {
  const data = await request.get<Driver>(`/drivers/profile/driver/`)
  return data
}

export const getMyCars = async (): Promise<AxiosResponse<DriverCar>> => {
  const data = await request.get<DriverCar>(`/drivers/profile/car/`)
  return data
}

export const getDriverCars = async (id: number): Promise<AxiosResponse<DriverCarList>> => {
  const data = await request.get<DriverCarList>(`/drivers/${id}/cars/`)
  return data
}

export const listDriverDocuments = async (id: number): Promise<AxiosResponse<DriverDocument[]>> => {
  const data = await request.get<DriverDocument[]>(`/drivers/${id}/documents/`)
  return data
}

export const getDriverDocument = async (
  driverId: number,
  documentType: DriverApplicationDocumentType,
): Promise<AxiosResponse<DriverApplicationDocument>> => {
  const data = await request.get<DriverApplicationDocument>(`/drivers/docs/${documentType}`, {
    params: { driver_id: driverId },
  })
  return data
}

export const getAllCars = async ({
  offset,
  limit,
}: {
  offset?: number
  limit?: number
} = {}): Promise<AxiosResponse<DriverCarList>> => {
  const params = new URLSearchParams()
  if (offset) params.append('offset', offset.toString())
  if (limit) params.append('limit', limit.toString())
  const response = await request.get<DriverCarList>(`/cars/?${params.toString()}`)
  return response
}

export const getCar = async (id: number): Promise<AxiosResponse<DriverCar>> => {
  const response = await request.get<DriverCar>(`/cars/${id}/`)
  return response
}

export const getCarPhotoByType = async (
  carId: number,
  photoType: string,
): Promise<AxiosResponse<{ filename: string; public_url: string }>> => {
  const response = await request.get<{ filename: string; public_url: string }>(`/cars/${carId}/photos/${photoType}`)
  return response
}

export const createCar = async (data: {
  driver_id?: number
  car_make: string
  car_model: string
  car_type: string
  year: number
  interior_color: string
  exterior_color: string
  has_child_seat: boolean
  has_booster_seat: boolean
  number_of_seats: number
  luggage_places: number
  profile_photo?: string
}): Promise<AxiosResponse<DriverCar>> => {
  const response = await request.post<DriverCar>(`/cars/`, data)
  return response
}

export const updateCar = async (
  carId: number,
  data: Partial<{
    driver_id: number
    car_make: string
    car_model: string
    car_type: string
    year: number
    interior_color: string
    exterior_color: string
    has_child_seat: boolean
    has_booster_seat: boolean
    number_of_seats: number
    luggage_places: number
    profile_photo?: string
  }>,
): Promise<AxiosResponse<DriverCar>> => {
  const response = await request.patch<DriverCar>(`/cars/${carId}/`, data)
  return response
}

export const uploadCarPhoto = async (
  driverId: number,
  carId: number,
  photoType: string,
  file: File,
): Promise<AxiosResponse<{ filename: string; public_url: string }>> => {
  const formData = new FormData()
  formData.append('photo', file)

  const response = await request.post<{ filename: string; public_url: string }>(
    `/drivers/${driverId}/cars/${carId}/photos/${photoType}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response
}

export const uploadCarPhotoByCarId = async (
  carId: number,
  photoType: string,
  file: File,
): Promise<AxiosResponse<{ filename: string; public_url: string }>> => {
  const formData = new FormData()
  formData.append('photo', file)

  const response = await request.post<{ filename: string; public_url: string }>(
    `/cars/${carId}/photos/${photoType}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response
}

export interface CarDocumentPayload {
  document?: File
  serial_number?: string
  cert_number?: string
  valid_from?: string
  exp_date?: string
}

export const listCarDocuments = async (carId: number): Promise<AxiosResponse<CarDocument[]>> => {
  const response = await request.get<CarDocument[]>(`/cars/${carId}/documents`)
  return response
}

export const uploadCarDocument = async (
  carId: number,
  documentType: string,
  data: CarDocumentPayload,
): Promise<AxiosResponse<{ filename: string; public_url: string }>> => {
  const formData = new FormData()
  if (data.document) formData.append('document', data.document)
  if (data.serial_number) formData.append('serial_number', data.serial_number)
  if (data.cert_number) formData.append('cert_number', data.cert_number)
  if (data.valid_from) formData.append('valid_from', data.valid_from)
  if (data.exp_date) formData.append('exp_date', data.exp_date)

  const response = await request.post<{ filename: string; public_url: string }>(
    `/cars/${carId}/documents/${documentType}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response
}

export const updateCarDocument = async (
  carId: number,
  documentType: string,
  data: CarDocumentPayload,
): Promise<AxiosResponse<{ filename: string; public_url: string }>> => {
  const formData = new FormData()
  if (data.document) formData.append('document', data.document)
  if (data.serial_number) formData.append('serial_number', data.serial_number)
  if (data.cert_number) formData.append('cert_number', data.cert_number)
  if (data.valid_from) formData.append('valid_from', data.valid_from)
  if (data.exp_date) formData.append('exp_date', data.exp_date)

  const response = await request.patch<{ filename: string; public_url: string }>(
    `/cars/${carId}/documents/${documentType}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response
}
